/**
 * ActeWorkflowFrise — la frise verticale du cycle de vie d'un acte.
 *
 * ─── POURQUOI CE COMPOSANT EXISTE ───────────────────────────────────────────
 *
 * Cette présentation vient de la fiche d'arrêté du service technique, et la
 * mairie l'a explicitement demandée partout (28/07) : *« j'aime bien cette
 * présentation, c'est celle qu'il faut de partout pour les arrêtés, en RH aussi,
 * pour le suivi, pour l'envoi »*.
 *
 * Elle était écrite en dur dans un seul écran. La sortir ici évite d'en avoir
 * trois versions qui divergent — le registre, le service technique et la RH
 * décrivent le même objet depuis la bascule B3/B3b.
 *
 * ─── CE QU'ELLE MONTRE, ET POURQUOI C'EST MIEUX QU'UNE LISTE ────────────────
 *
 * Une liste dit « signé le 3 mars ». La frise dit **où on en est** : ce qui est
 * fait, ce qui est en cours, ce qui reste. Sur un acte contesté, c'est aussi
 * le parcours qu'on doit pouvoir reconstituer d'un coup d'œil.
 *
 * Un acte ANNULÉ n'affiche aucune étape en cours : il est sorti du circuit.
 */

import { CheckCircle2 } from 'lucide-react';

export interface EtapeActe {
  label: string;
  /** Qui, ou à défaut à quoi sert l'étape. */
  sub: string;
  date: string | null;
}

/** L'ordre du circuit. `annule` n'en fait pas partie : c'est une sortie. */
export const ORDRE_STATUTS = [
  'genere', 'relecture_juridique', 'a_signer', 'signe', 'publie',
] as const;

export type StatutFrise = (typeof ORDRE_STATUTS)[number] | 'annule';

function frDateHeure(iso: string | null): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Paris',
  }).format(new Date(iso));
}

export function ActeWorkflowFrise({
  statut,
  etapes,
}: {
  statut: StatutFrise;
  etapes: EtapeActe[];
}) {
  const courant = (ORDRE_STATUTS as readonly string[]).indexOf(statut);

  function etat(i: number): 'done' | 'current' | 'pending' {
    // Un acte annulé n'a plus d'étape « en cours » : il est sorti du circuit.
    if (statut === 'annule') return 'pending';
    if (i < courant) return 'done';
    if (i === courant) return 'current';
    return 'pending';
  }

  return (
    <ol className="space-y-3">
      {etapes.map((step, i) => {
        const e = etat(i);
        return (
          <li key={step.label} className="flex gap-2.5">
            <div className="flex flex-col items-center" aria-hidden="true">
              <span
                className={[
                  'flex h-5 w-5 items-center justify-center rounded-full border-2 ring-4 ring-bg',
                  e === 'done'
                    ? 'border-success bg-success'
                    : e === 'current'
                      ? 'border-warning bg-warning'
                      : 'border-border bg-surface',
                ].join(' ')}
                style={
                  e === 'current'
                    ? { boxShadow: '0 0 0 4px oklch(var(--c-warning) / 0.15)' }
                    : undefined
                }
              >
                {e === 'done' && <CheckCircle2 className="h-3 w-3 text-white" aria-hidden="true" />}
              </span>
              {i < etapes.length - 1 && (
                <span
                  className={`mt-1 w-0.5 flex-1 ${e === 'done' ? 'bg-success/50' : 'bg-border'}`}
                  style={{ minHeight: 24 }}
                />
              )}
            </div>
            <div className="flex-1 pb-1">
              <div className={`text-sm ${e === 'pending' ? 'text-ink-subtle' : 'font-medium text-ink'}`}>
                {step.label}
              </div>
              <div className="text-xs text-ink-subtle">
                {step.sub}
                {step.date && ` · ${frDateHeure(step.date)}`}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
